/**
 * Re-read on-chain state after a transaction. A public RPC is a load balancer: the receipt can come from a node that
 * has the new block while the re-read lands on one that does not yet, and the pre-transaction balances would stay on
 * screen until the next poll. So: wait (bounded) until the RPC reports the transaction's block, re-read, and re-read
 * again a little later for a straggling node.
 */
export const FOLLOW_UPS_MS = [2_000, 5_000] as const;

export async function refreshAfter(
  invalidate: () => Promise<unknown>,
  opts: { block?: bigint; head?: () => Promise<bigint>; timeoutMs?: number; pollMs?: number; sleep?: (ms: number) => Promise<void> } = {},
): Promise<void> {
  const sleep = opts.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
  const { block, head } = opts;
  if (block !== undefined && head) {
    const pollMs = opts.pollMs ?? 400;
    for (let waited = 0; waited < (opts.timeoutMs ?? 10_000); waited += pollMs) {
      try {
        if ((await head()) >= block) break;
      } catch {
        /* a failed probe is not a reason to skip the refresh */
      }
      await sleep(pollMs);
    }
  }
  await invalidate();
  for (const ms of FOLLOW_UPS_MS) void sleep(ms).then(invalidate);
}

/** Re-reads `read` until `ok` holds (an approval's allowance showing on the RPC), at most `tries` times. */
export async function readUntil<T>(read: () => Promise<T>, ok: (v: T) => boolean, opts: { tries?: number; pollMs?: number; sleep?: (ms: number) => Promise<void> } = {}): Promise<T> {
  const sleep = opts.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
  let v = await read();
  for (let i = 1; i < (opts.tries ?? 12) && !ok(v); i++) {
    await sleep(opts.pollMs ?? 1_000);
    v = await read();
  }
  return v;
}
